/**
 * hyperdrive-time.ts — per-class charge-up + per-jump travel time
 * helpers (v4M5.2c).
 *
 * Two derivations live here, both keyed off `docs/HYPERSPACE.md`:
 *
 *   1. **Charge-up ticks** per `§6`. Locked per class:
 *      - Light  (Scout / Corvette / Courier)                 = 40 ticks (4 s @ 10 Hz)
 *      - Medium (Destroyer / Cruiser / Constructor)          = 60 ticks (6 s)
 *      - Heavy  (Carrier / Battleship / Capital)             = 80 ticks (8 s)
 *
 *      Effective charge-up for a fleet = max across ships (slowest sets
 *      the pace, mirroring the legacy `jumpDurationTicks` semantic).
 *      Chassis not in the explicit table default to Medium — a safe
 *      middle ground for utility / niche chassis (Coloniser, Engineer,
 *      Ranger, Mechanic, Infiltrator, Gunship, etc.).
 *
 *   2. **Travel time ticks** per `§1`:
 *
 *        travelTime (ticks) = ceil(pathDistance / (hyperdriveSpeed × empireSpeedMul))
 *
 *      Floor at 1 tick — even a tiny adjacent-system hop costs at
 *      least one sim tick of in-hyperspace transit. Authored chassis
 *      hyperdriveSpeed values (units per tick @ 10 Hz) are typically
 *      0.001–0.05, so a typical 0.30-unit jump runs 6–300 ticks.
 *
 * v4M5.2c combines charge-up + travel time into a single
 * `jumpTicksRemaining` countdown — the existing single-counter
 * pipeline doesn't need refactoring yet. v4M5.3 splits them once the
 * on-map ring-point render lands and the vulnerability semantics
 * matter (a charging ship is on-map; an in-hyperspace ship isn't).
 */

/**
 * Per-class charge-up tiers (ticks at 10 Hz). Locked per §6.
 */
export const CHARGE_UP_LIGHT_TICKS = 40;
export const CHARGE_UP_MEDIUM_TICKS = 60;
export const CHARGE_UP_HEAVY_TICKS = 80;

/**
 * Chassis → charge-up ticks table per `HYPERSPACE.md §6`.
 *
 * Unlisted chassis fall through to `CHARGE_UP_MEDIUM_TICKS` per the
 * §6 spec's "fleet uses the slowest" rule biased toward generosity
 * for utility ships.
 */
const CHASSIS_CHARGE_UP_TICKS: Record<string, number> = {
  // Light tier — recon + light attack
  Scout: CHARGE_UP_LIGHT_TICKS,
  Corvette: CHARGE_UP_LIGHT_TICKS,
  Courier: CHARGE_UP_LIGHT_TICKS,
  Ranger: CHARGE_UP_LIGHT_TICKS, // RACE-BB Scout equivalent
  Infiltrator: CHARGE_UP_LIGHT_TICKS, // RACE-CC cloaked light
  // Medium tier — workhorses
  Destroyer: CHARGE_UP_MEDIUM_TICKS,
  Cruiser: CHARGE_UP_MEDIUM_TICKS,
  Constructor: CHARGE_UP_MEDIUM_TICKS,
  // Heavy tier — capital ships
  Carrier: CHARGE_UP_HEAVY_TICKS,
  Battleship: CHARGE_UP_HEAVY_TICKS,
  Capital: CHARGE_UP_HEAVY_TICKS,
  Flagship: CHARGE_UP_HEAVY_TICKS,
};

/**
 * Charge-up ticks for a single ship by chassis name. Returns
 * `CHARGE_UP_MEDIUM_TICKS` for chassis not in the explicit table.
 */
export function chargeUpTicksForChassis(chassis: string): number {
  return CHASSIS_CHARGE_UP_TICKS[chassis] ?? CHARGE_UP_MEDIUM_TICKS;
}

/**
 * Compute the per-jump travel time in sim ticks. Floor at 1 tick so
 * even adjacent-system hops cost at least one frame of transit.
 *
 * Caller is responsible for `hyperdriveSpeedMul = hyperdriveSpeedMultiplier(state, empire)`
 * (the empire's Hyperdrive Speed Mk research multiplier).
 */
export function travelTicksFor(
  pathDistance: number,
  hyperdriveSpeed: number,
  hyperdriveSpeedMul: number,
): number {
  if (hyperdriveSpeed <= 0 || hyperdriveSpeedMul <= 0) return 1;
  const ticks = Math.ceil(pathDistance / (hyperdriveSpeed * hyperdriveSpeedMul));
  return Math.max(1, ticks);
}
